import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Layers, Trash2, Plus, LayoutTemplate } from 'lucide-react';
import { toast } from 'sonner';
import {
  getMilestoneTemplates,
  applyTemplateToProject,
  saveProjectAsTemplate,
  deleteMilestoneTemplate,
} from '@/lib/api';

/**
 * ApplyTemplateDialog — pick a saved template and apply it to a
 * renovation project. Offers "append" (default) or "replace" mode.
 */
export const ApplyTemplateDialog = ({ open, onOpenChange, project, onApplied }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('append');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await getMilestoneTemplates();
      setTemplates(data || []);
    } catch (err) {
      console.warn('Failed to load templates', err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      load();
      setSelectedId(null);
      setMode('append');
    }
  }, [open, load]);

  const handleApply = async () => {
    if (!selectedId) {
      toast.error('Select a template');
      return;
    }
    setBusy(true);
    try {
      const { data } = await applyTemplateToProject(project.id, selectedId, mode);
      const applied = templates.find((t) => t.id === selectedId);
      toast.success(
        `Applied "${applied?.name || 'template'}" — ${applied?.items?.length || 0} milestones added`
      );
      onApplied?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await deleteMilestoneTemplate(t.id);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      if (selectedId === t.id) setSelectedId(null);
      toast.success('Template deleted');
    } catch (err) {
      console.warn('Failed to delete template', err);
      toast.error('Failed to delete');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[520px] max-h-[80vh] overflow-y-auto"
        data-testid="apply-template-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-manrope flex items-center gap-2">
            <LayoutTemplate className="h-5 w-5 text-slate-700" />
            Apply Milestone Template
          </DialogTitle>
        </DialogHeader>

        {templates.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-400">
            You haven&apos;t saved any templates yet. Add milestones to a
            project, then click <b>Save as Template</b> to create one.
          </div>
        ) : (
          <>
            <div className="space-y-2 my-2 max-h-[300px] overflow-y-auto">
              {templates.map((t) => {
                const isSel = t.id === selectedId;
                return (
                  <div
                    key={t.id}
                    className={`rounded-lg border transition-colors ${
                      isSel
                        ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setSelectedId(t.id)}
                        className="flex-1 text-left min-w-0"
                        data-testid={`template-option-${t.id}`}
                      >
                        <p className="font-medium text-slate-900">{t.name}</p>
                        {t.description && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                            {t.description}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          {t.items?.length || 0} milestones
                          {t.items?.length > 0 && (
                            <span className="ml-1">
                              · {t.items[0].title}
                              {t.items.length > 1 && ` +${t.items.length - 1} more`}
                            </span>
                          )}
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(t)}
                        className="p-1 rounded text-red-500 hover:bg-red-50 shrink-0"
                        data-testid={`delete-template-${t.id}`}
                        aria-label={`Delete template ${t.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t pt-3">
              <Label className="text-xs uppercase tracking-wide text-slate-500 mb-2 block">
                Apply mode
              </Label>
              <RadioGroup value={mode} onValueChange={setMode} className="space-y-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="append" data-testid="apply-mode-append" />
                  <span>
                    <b>Append</b> — add these milestones after existing ones
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="replace" data-testid="apply-mode-replace" />
                  <span className="text-red-600">
                    <b>Replace</b> — remove existing milestones first
                  </span>
                </label>
              </RadioGroup>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={busy || !selectedId}
            onClick={handleApply}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="apply-template-btn"
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * SaveAsTemplateDialog — capture the current project's milestones
 * as a reusable template.
 */
export const SaveAsTemplateDialog = ({ open, onOpenChange, project }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(project?.title ? `${project.title} template` : '');
      setDescription('');
    }
  }, [open, project]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Name required');
      return;
    }
    if (!project?.milestones?.length) {
      toast.error('Project has no milestones to save');
      return;
    }
    setBusy(true);
    try {
      await saveProjectAsTemplate(project.id, name.trim(), description.trim());
      toast.success(`Template "${name.trim()}" saved — reuse it on any future project`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="save-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope flex items-center gap-2">
            <Layers className="h-5 w-5 text-slate-700" />
            Save as Milestone Template
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <p className="text-sm text-slate-500">
            Captures {project?.milestones?.length || 0} milestones as a
            reusable template. Target dates are stored as offsets from
            the project&apos;s start date.
          </p>

          <div className="space-y-2">
            <Label>Template Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Kitchen Remodel"
              data-testid="template-name-input"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this template is best for..."
              data-testid="template-description-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={busy}
            onClick={handleSave}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="save-template-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Save Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
